/**
 * anchorClarifications — clarification builders for the new cascade
 * types introduced by gibbs-clarify-on-ambiguous-target.
 *
 * Three new cascades:
 *   - target_selection      — user mentioned a domain; pick a target from
 *                             that domain's known list
 *   - target_confirmation   — workspace protein IS relevant to the stated
 *                             domain; one-question yes/no/redirect
 *   - generic_target        — no domain, no anchor — user types a target
 *                             or picks a disease
 *
 * @module gibbs/text-to-action/anchorClarifications
 */

import { diseaseTargets } from '../knowledge/diseaseTargets';

const MAX_TARGET_OPTIONS = 6;

function formatTargetLabel(target) {
    const gene = target.geneSymbol || '';
    const name = target.name || '';
    const pdb = (Array.isArray(target.pdbIds) && target.pdbIds[0]) || '';
    if (gene && pdb) return `${gene} (${pdb}) — ${name}`.trim();
    if (gene) return `${gene} — ${name}`.trim();
    return name || target.uniprot || 'unknown target';
}

/**
 * Build a target-selection clarification listing topTargets for a domain.
 * Distinct cascade name from the existing `disease_target_selection` —
 * this one is emitted by the new anchor resolver, not by the existing
 * disease-design-goal cascade.
 *
 * @param {string} domainSlug
 * @returns {Object} clarify-intent object
 */
export function buildTargetSelectionClarification(domainSlug) {
    const entry = diseaseTargets && diseaseTargets.diseases
        && diseaseTargets.diseases[domainSlug];
    if (!entry) {
        return {
            intent: 'clarify',
            cascade: 'target_selection',
            question: 'Which target should this workflow run against?',
            options: [],
            context: { cascade: 'target_selection', domainSlug },
        };
    }
    const targets = (entry.topTargets || []).slice(0, MAX_TARGET_OPTIONS);

    const options = targets.map((t) => ({
        label: formatTargetLabel(t),
        value: {
            cascade: 'target_selection',
            domainSlug,
            target: {
                pdbId: (Array.isArray(t.pdbIds) && t.pdbIds[0]) || null,
                uniprot: t.uniprot || null,
                name: t.geneSymbol || t.name || null,
            },
        },
    }));

    return {
        intent: 'clarify',
        cascade: 'target_selection',
        question:
            `Which ${entry.canonicalName} target would you like to design against?`,
        options,
        context: { cascade: 'target_selection', domainSlug },
    };
}

/**
 * Build a target-confirmation clarification — single yes/no/redirect.
 *
 * @param {{ candidate: Object, domainSlug: string, evidence: string }} args
 * @returns {Object} clarify-intent object
 */
export function buildTargetConfirmationClarification({ candidate, domainSlug, evidence }) {
    const entry = diseaseTargets && diseaseTargets.diseases
        && diseaseTargets.diseases[domainSlug];
    const domainName = (entry && entry.canonicalName) || domainSlug;
    const candidateLabel = (candidate && (candidate.name || candidate.pdbId)) || 'the loaded protein';

    const question = `${candidateLabel} is loaded and is a known target for ${domainName} `
        + `(${evidence || 'local data'}). Use it for this workflow?`;

    return {
        intent: 'clarify',
        cascade: 'target_confirmation',
        question,
        candidate,
        evidence,
        domainSlug,
        options: [
            {
                label: `Use ${candidateLabel}`,
                value: { cascade: 'target_confirmation', action: 'use_candidate' },
            },
            {
                label: 'Choose a different target',
                value: { cascade: 'target_confirmation', action: 'choose_different' },
            },
            {
                label: 'Tell me more',
                value: { cascade: 'target_confirmation', action: 'tell_more' },
            },
        ],
        context: {
            cascade: 'target_confirmation', candidate, domainSlug, evidence,
        },
    };
}

/**
 * Build a generic target-selection clarification — used when there's
 * no domain term AND no workspace protein to anchor on.
 *
 * @returns {Object} clarify-intent object
 */
export function buildGenericTargetClarification() {
    return {
        intent: 'clarify',
        cascade: 'generic_target',
        question:
            'Which target should this workflow run against? You can name a '
            + 'protein, a PDB ID, or a disease.',
        options: [
            {
                label: 'Type a protein or PDB ID',
                value: { cascade: 'generic_target', action: 'type_target' },
            },
            {
                label: 'Pick a disease',
                value: { cascade: 'generic_target', action: 'pick_disease' },
            },
        ],
        context: { cascade: 'generic_target' },
    };
}

export default {
    buildTargetSelectionClarification,
    buildTargetConfirmationClarification,
    buildGenericTargetClarification,
};
